/**
 * Monthly Calendar View Component
 *
 * Renders monthly calendar grid using react-native-calendars.
 * Shows event titles as text bars on each day via custom dayComponent.
 *
 * Follows single responsibility principle: only renders calendar grid UI.
 */

import { useCallback, useMemo, useState } from 'react';
import { StyleSheet, Text, View, TouchableOpacity } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { Calendar, DateData } from 'react-native-calendars';
import { useColorScheme } from '@/hooks/use-color-scheme';
import { MonthlyCalendarTheme, calculateCalendarGridHeight, CALENDAR_HORIZONTAL_PADDING } from '@/lib/calendar/MonthlyCalendarTheme';
import { MonthlyCalendarManager } from '@/lib/calendar/MonthlyCalendarManager';
import { CalendarDayCell } from './CalendarDayCell';
import { AddEventDropdown } from './AddEventDropdown';
import { ScreenHeaderContainer } from '@/components/ui/ScreenHeaderContainer';
import { OneTimeCallout } from '@/components/shared/OneTimeCallout';
import { useOneTimeCallout } from '@/hooks/use-one-time-callout';
import { Colors } from '@/constants/theme';
import { Spacing, BorderRadius } from '@/constants/spacing';
import { getHeadingFont } from '@/constants/typography';
import type { MonthlyCalendarViewProps } from '@/types/monthly-calendar';


/**
 * MonthlyCalendarView Component
 *
 * Renders the monthly calendar grid with event text bars.
 */
export function MonthlyCalendarView({
  markedDates,
  selectedDate,
  onDateSelect,
  onMonthChange,
  accentColor,
  onMenuPress,
  onTodayPress,
  onAddEventPress,
  showCalendarSelector,
  calendarKey,
}: MonthlyCalendarViewProps) {
  const colorScheme = useColorScheme();
  const { isCalloutVisible, dismissCallout } = useOneTimeCallout('fimfam-ai:callout:monthly-menu');

  // Parse selected date to Date object
  const selectedDateObject = useMemo(() => {
    const [year, month, day] = selectedDate.split('-').map(Number);
    return new Date(year, month - 1, day);
  }, [selectedDate]);

  // Calculate number of week rows needed for this month
  const weekRows = useMemo(() => {
    const [year, month] = selectedDate.split('-').map(Number);
    return MonthlyCalendarManager.calculateWeekRows(year, month - 1);
  }, [selectedDate]);

  // Calculate dynamic calendar grid height based on week rows
  const calendarGridHeight = useMemo(() => {
    return calculateCalendarGridHeight(weekRows);
  }, [weekRows]);

  // Get theme configuration
  const theme = useMemo(() => {
    return MonthlyCalendarTheme.getTheme(colorScheme ?? 'light', accentColor);
  }, [colorScheme, accentColor]);

  const customStyles = useMemo(() => {
    return MonthlyCalendarTheme.getCustomStyles(colorScheme ?? 'light', accentColor);
  }, [colorScheme, accentColor]);

  const handleMenuPress = useCallback(() => {
    dismissCallout();
    onMenuPress();
  }, [dismissCallout, onMenuPress]);

  // Handle date selection from react-native-calendars
  const handleDayPress = useCallback((day: DateData) => {
    onDateSelect(day.dateString);
  }, [onDateSelect]);

  // Handle month change from react-native-calendars
  const handleMonthChange = useCallback((month: DateData) => {
    const newDate = new Date(month.year, month.month - 1, 1);
    onMonthChange(newDate);
  }, [onMonthChange]);

  // Format month/year text for header
  const monthYearText = useMemo(() => {
    return selectedDateObject.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  }, [selectedDateObject]);

  // react-native-calendars treats `current` as an INITIAL date and does not
  // re-navigate when the prop changes. Swiping works because the library
  // navigates internally first, then fires onMonthChange to sync our state.
  // For our custom arrow buttons we need to force a remount via the key so
  // the calendar picks up the new `current`. Bumping this counter only on
  // arrow presses keeps swipe behavior smooth (no remount mid-animation).
  const [manualNavCount, setManualNavCount] = useState(0);

  // Navigate to previous month
  const handlePrevMonth = useCallback(() => {
    const newDate = new Date(selectedDateObject);
    newDate.setMonth(newDate.getMonth() - 1);
    setManualNavCount((c) => c + 1);
    onMonthChange(newDate);
  }, [selectedDateObject, onMonthChange]);

  // Navigate to next month
  const handleNextMonth = useCallback(() => {
    const newDate = new Date(selectedDateObject);
    newDate.setMonth(newDate.getMonth() + 1);
    setManualNavCount((c) => c + 1);
    onMonthChange(newDate);
  }, [selectedDateObject, onMonthChange]);

  // Custom day component renderer
  const renderDayComponent = useCallback((props: any) => {
    return (
      <CalendarDayCell
        {...props}
        onPress={handleDayPress}
        accentColor={accentColor}
        colorScheme={colorScheme ?? 'light'}
        customStyles={customStyles}
      />
    );
  }, [handleDayPress, accentColor, colorScheme, customStyles]);

  const palette = Colors[colorScheme ?? 'light'];

  return (
    <View style={[styles.container, { backgroundColor: palette.background }]}>
      {/* Month Navigation Header */}
      <ScreenHeaderContainer style={styles.monthHeader} flatBottom>
        {/* Left: Menu Button */}
        <View style={styles.leftButtons}>
          <TouchableOpacity
            style={styles.navButton}
            onPress={handleMenuPress}
            activeOpacity={0.7}
          >
            <Ionicons
              name={showCalendarSelector ? 'close' : 'menu'}
              size={24}
              color={palette.tint}
            />
          </TouchableOpacity>
        </View>

        {/* Center: Navigation Group */}
        <View style={styles.monthNavigationGroup}>
          {/* Left Arrow */}
          <TouchableOpacity
            style={styles.navButton}
            onPress={handlePrevMonth}
            activeOpacity={0.7}
          >
            <Ionicons name="chevron-back" size={24} color={palette.tint} />
          </TouchableOpacity>

          {/* Month/Year */}
          <Text
            style={[styles.monthTitle, { color: palette.text, fontFamily: getHeadingFont() }]}
            numberOfLines={1}
            adjustsFontSizeToFit
            minimumFontScale={0.8}
          >
            {monthYearText}
          </Text>

          {/* Right Arrow */}
          <TouchableOpacity
            style={styles.navButton}
            onPress={handleNextMonth}
            activeOpacity={0.7}
          >
            <Ionicons name="chevron-forward" size={24} color={palette.tint} />
          </TouchableOpacity>
        </View>

        {/* Right: Add and Today Buttons */}
        <View style={styles.rightButtons}>
          {onAddEventPress && (
            <AddEventDropdown
              onAddEvent={onAddEventPress}
              tintColor={palette.tint}
            />
          )}
          <TouchableOpacity
            style={[styles.todayButton, { borderColor: palette.tint }]}
            onPress={() => {
              setManualNavCount((c) => c + 1);
              onTodayPress();
            }}
            activeOpacity={0.7}
          >
            <Text style={[styles.todayButtonText, { color: palette.tint }]}>
              {new Date().getDate()}
            </Text>
          </TouchableOpacity>
        </View>
      </ScreenHeaderContainer>

      <View style={styles.calloutAnchor} pointerEvents="box-none">
        <OneTimeCallout
          visible={isCalloutVisible}
          text="Set up family members and change views here"
          onDismiss={dismissCallout}
          pointer="up"
          pointerAlign="left"
        />
      </View>

      {/* Spacer so the header's shadow has room to breathe before the grid */}
      <View style={styles.headerShadowSpacer} />

      {/* Calendar Grid */}
      <View style={{ paddingHorizontal: CALENDAR_HORIZONTAL_PADDING }}>
        <Calendar
          key={`${calendarKey}-${colorScheme}-${manualNavCount}`}
          current={selectedDate}
          markedDates={markedDates}
          onDayPress={handleDayPress}
          onMonthChange={handleMonthChange}
          enableSwipeMonths={true}
          firstDay={1}
          hideExtraDays={false}
          showSixWeeks={false}
          hideArrows={true}
          renderHeader={() => null}
          dayComponent={renderDayComponent}
          theme={theme as any}
          style={{ height: calendarGridHeight, backgroundColor: palette.cardBackground }}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    width: '100%',
  },
  headerShadowSpacer: {
    height: Spacing.md,
  },
  calloutAnchor: {
    position: 'relative',
    zIndex: 101,
  },
  monthHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: Spacing.md,
    paddingVertical: Spacing.md + 2, // 14px
  },
  leftButtons: {
    width: 44,
    alignItems: 'flex-start',
  },
  monthNavigationGroup: {
    flexDirection: 'row',
    alignItems: 'center',
    flex: 1,
    justifyContent: 'center',
    marginHorizontal: Spacing.xs,
  },
  navButton: {
    padding: Spacing.sm, // normalized from 6
    justifyContent: 'center',
    alignItems: 'center',
  },
  monthTitle: {
    fontSize: 18,
    fontWeight: '600',
    paddingHorizontal: Spacing.xs,
    textAlign: 'center',
    flexShrink: 1,
  },
  rightButtons: {
    flexDirection: 'row',
    gap: Spacing.sm,
    alignItems: 'center',
  },
  todayButton: {
    width: 34,
    height: 34,
    borderRadius: BorderRadius.sm,
    borderWidth: 1.5,
    justifyContent: 'center',
    alignItems: 'center',
  },
  todayButtonText: {
    fontSize: 12,
    fontWeight: '600',
  },
});
